import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/apiClient";
import { JusticeSpeech } from "@/components/Justice";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | ok | timeout
  const [kind, setKind] = useState(null);
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("timeout"); return; }
    let tries = 0;
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { if (active) { setKind(data.kind || null); setCredits(data.credits || null); setState("ok"); } return; }
      } catch (e) { console.error("payment status poll failed", e); }
      tries += 1;
      if (tries >= 8) { if (active) setState("timeout"); return; }
      if (active) setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  const copy = {
    case_prep_pass: { title: "Case Prep Pass unlocked", msg: "Your final, court-ready exports for this case are now unlocked — the PREVIEW watermark is removed and the clickable Table of Contents is enabled. Head back to Reports to generate your final PDF." },
    credit_pack: { title: "KeeperCredits added", msg: `${credits ? credits + " " : ""}KeeperCredits have been added to your balance. They never expire and are spent after your monthly credits.` },
    default: { title: "Welcome to the Vault", msg: "Your 7-day free trial is active. I'll count down the days with you — cancel anytime before it ends and your card won't be charged." },
  };
  const c = copy[kind] || copy.default;

  return (
    <div className="min-h-screen flex items-center justify-center p-8 ek-scanline">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg border rounded-lg p-8" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="payment-success">
        {state === "checking" && (
          <div className="flex items-center gap-3" style={{ color: "var(--ek-text-secondary)" }}>
            <Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--ek-primary)" }} /> Confirming your purchase…
          </div>
        )}
        {state === "ok" && (
          <>
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="h-6 w-6" style={{ color: "var(--ek-secure)" }} />
              <h1 className="font-head font-bold text-2xl tracking-tight" data-testid="success-title">{c.title}</h1>
            </div>
            <JusticeSpeech size={110} message={c.msg} />
            <Button onClick={() => navigate("/")} data-testid="success-continue-btn" className="w-full rounded-md mt-8" style={{ background: "var(--ek-primary)", color: "#fff" }}>
              Enter EvidenceKeeper
            </Button>
          </>
        )}
        {state === "timeout" && (
          <>
            <h1 className="font-head font-bold text-xl tracking-tight mb-2">Almost there</h1>
            <p className="text-sm mb-6" style={{ color: "var(--ek-text-secondary)" }}>We're still confirming your purchase with Stripe. This can take a moment — you can continue and it'll update shortly.</p>
            <Button onClick={() => navigate("/")} data-testid="success-continue-btn" className="w-full rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>Continue</Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
